import { useContext, useState, useEffect, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import yup from 'utils/yup';
import { diContainerContext } from 'application/contexts/useDiContainer';
import { BuyerUserQuery } from 'interfaceAdapter/queries/BuyerUser';
import { BuyerRegistrationProfileInfoQuery } from 'interfaceAdapter/queries/BuyerRegistrationProfileInfo';
import { BuyerEditRepository } from 'interfaceAdapter/repositories/BuyerEdit';
import { useNavigate } from 'react-router-dom';
import { buyerUserContext } from 'application/contexts/useBuyerUser';
import { CompanySearchResult } from 'domain/entities/CompanySearchResult/CompanySearchResult';
import { IAreaOption } from 'domain/entities/Buyer/Buyer';
import { useLoading } from 'application/contexts/useLoading';

const schema = yup
    .object({
        name: yup.string().default('').required(),
        company_code: yup
            .string()
            .matches(/^[0-9]+$/, '半角数字のみ入力可能です')
            .default(''),
        is_sole_proprietor: yup.boolean().required().default(false),
        target_area: yup.array().of(yup.string()).required(), // 買収希望案件エリア
        target_company_industries: yup.array().of(yup.string()).required(), // 買収希望案件業種
        target_company_employee_size_lower: yup.string().default(''), // 買収希望案件の従業員数（下限）
        target_company_employee_size_upper: yup.string().default(''), // 買収希望案件の従業員数（上限）
        target_company_purchase_amount_lower: yup.string().default(''), // 買収希望金額（下限）
        target_company_purchase_amount_upper: yup.string().default(''), // 買収希望金額（上限）
        target_company_revenue_scale_lower: yup.string().default(''), // 買収希望案件の売上高（下限）
        target_company_revenue_scale_upper: yup.string().default(''), // 買収希望案件の売上高（上限）
        target_company_profit_level_lower: yup.string().default(''), // 買収希望案件の営業利益（下限）
        target_company_profit_level_upper: yup.string().default(''), // 買収希望案件の営業利益（上限）
        target_company_net_assets_level_lower: yup.string().default(''), // 買収希望案件の純資産金額（下限）
        target_company_net_assets_level_upper: yup.string().default(''), // 買収希望案件の純資産金額（上限）
        has_acquisition_experience: yup.string().default(''), // 過去の買収実績があるか
        other_buying_points: yup.string().default('').max(1000), // その他希望事項等
        target_company_industry_details: yup.string().default('').max(2000), // 買収希望案件の業種詳細
        number_of_employees: yup.string().default(''),
        revenue_scale: yup.string().default(''),
        business_description: yup.string().default(''),
        question: yup.string().default(''),
        icon_image: yup.string().default(''),
        hp_url: yup.string().default(''),
        fee_structure: yup.boolean().required(), // M&Aに関する手数料水準について
        other_buyers: yup.boolean().required(), // 他の買い手候補について
        contract_status: yup.boolean().required(), // 提案案件の契約状況について
        process_flow: yup.boolean().required(), // 具体的な流れについて
        business_synergy: yup.boolean().required(), // 貴社の想定する事業シナジー、事業展望、スキーム等について
        support_system: yup.boolean().required(), // 支援体制、サポート内容について
        fee_structure_txt: yup.string().default('').max(1000), // M&Aに関する手数料水準について（詳細）
        other_buyers_txt: yup.string().default('').max(1000), // 他の買い手候補について（詳細）
        contract_status_txt: yup.string().default('').max(1000), // 提案案件の契約状況について（詳細）
        process_flow_txt: yup.string().default('').max(1000), // 具体的な流れについて（詳細）
        business_synergy_txt: yup.string().default('').max(1000), // 貴社の想定する事業シナジー、事業展望、スキーム等について（詳細）
        support_system_txt: yup.string().default('').max(1000), // 支援体制、サポート内容について（詳細）
    })
    .required();

type BuyerForm = yup.InferType<typeof schema>;

export const useApplicationForm = () => {
    const diContainer = useContext(diContainerContext);
    const buyerRegistrationProfileInfoQuery = diContainer.resolve(BuyerRegistrationProfileInfoQuery);
    const buyerUserQuery = diContainer.resolve(BuyerUserQuery);
    const buyerEditRepository = diContainer.resolve(BuyerEditRepository);
    const { loading, setLoading } = useLoading();
    const [areaChoices, setAreaChoices] = useState<IAreaOption[]>([]);
    const [employeeChoices, setEmployeeChoices] = useState<string[][]>([]);
    const [revenueChoices, setRevenueChoices] = useState<string[][]>([]);
    const [industryChoices, setIndustryChoices] = useState<string[][]>([]);
    const [employeeLowerChoices, setEmployeeLowerChoices] = useState<string[][]>([]);
    const [employeeUpperChoices, setEmployeeUpperChoices] = useState<string[][]>([]);
    const [netAssetsLevelLowerChoices, setNetAssetsLevelLowerChoices] = useState<string[][]>([]);
    const [netAssetsLevelUpperChoices, setNetAssetsLevelUpperChoices] = useState<string[][]>([]);
    const [purchaseAmountLowerChoices, setPurchaseAmountLowerChoices] = useState<string[][]>([]);
    const [purchaseAmountUpperChoices, setPurchaseAmountUpperChoices] = useState<string[][]>([]);
    const [revenueScaleLowerChoices, setRevenueScaleLowerChoices] = useState<string[][]>([]);
    const [revenueScaleUpperChoices, setRevenueScaleUpperChoices] = useState<string[][]>([]);
    const [profitLevelLowerChoices, setProfitLevelLowerChoices] = useState<string[][]>([]);
    const [profitLevelUpperChoices, setProfitLevelUpperChoices] = useState<string[][]>([]);
    const [hasAcquisitionExperienceChoices, setHasAcquisitionExperienceChoices] = useState<string[][]>([]);
    const [showSnackbar, setShowSnackbar] = useState(false);
    const [shwAgreementDialog, setShwAgreementDialog] = useState(false);
    const navigate = useNavigate();
    const { user, setUser } = useContext(buyerUserContext);

    const {
        control,
        handleSubmit,
        formState: { errors },
        getValues,
        watch,
        setValue,
    } = useForm<BuyerForm>({
        resolver: yupResolver(schema),
        mode: 'onChange',
        defaultValues: {
            name: '',
            company_code: '',
            is_sole_proprietor: false,
            icon_image: '',
            target_area: [],
            target_company_industries: [],
            number_of_employees: '',
            target_company_employee_size_lower: '',
            target_company_employee_size_upper: '',
            target_company_purchase_amount_lower: '',
            target_company_purchase_amount_upper: '',
            target_company_revenue_scale_lower: '',
            target_company_revenue_scale_upper: '',
            target_company_profit_level_lower: '',
            target_company_profit_level_upper: '',
            target_company_net_assets_level_lower: '',
            target_company_net_assets_level_upper: '',
            target_company_industry_details: '',
            other_buying_points: '',
            business_description: '',
            revenue_scale: '',
            hp_url: '',
            question: '',
            fee_structure: false,
            other_buyers: false,
            contract_status: false,
            process_flow: false,
            business_synergy: false,
            support_system: false,
            fee_structure_txt: '',
            other_buyers_txt: '',
            contract_status_txt: '',
            process_flow_txt: '',
            business_synergy_txt: '',
            support_system_txt: '',
        },
    });

    const refetchUser = useCallback(async () => {
        const { user, status } = await buyerUserQuery.checkToken(true);
        if (status !== false) {
            setUser(user);
        }
    }, []);

    const onCompanySelect = (buyer: CompanySearchResult) => {
        setValue('name', buyer.name);
        setValue('company_code', buyer.number);
    };

    useEffect(() => {
        const checkCanEnter = async () => {
            const { user, status } = await buyerUserQuery.checkToken(true);

            if (status === false) {
                // 有効なトークンが無いのでリダイレクト
                navigate('/buyer/login/');
                return;
            }
            setUser(user);
        };
        checkCanEnter();
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            const data = await buyerRegistrationProfileInfoQuery.fetch();
            setAreaChoices(data.area_choices);
            setEmployeeChoices(data.employee_choices);
            // TODO:「99:5億円～」は旧選択肢保持の為、除外。該当データがなくなれば削除
            setRevenueChoices(data.revenue_choices.filter((choice) => choice[0] !== '99'));
            setEmployeeLowerChoices(data.employee_lower_choices);
            setEmployeeUpperChoices(data.employee_upper_choices);
            setRevenueScaleLowerChoices(data.revenue_scale_lower_choices);
            setRevenueScaleUpperChoices(data.revenue_scale_upper_choices);
            setPurchaseAmountLowerChoices(data.purchase_amount_lower_choices);
            setPurchaseAmountUpperChoices(data.purchase_amount_upper_choices);
            setIndustryChoices(data.industry_choices);
            setProfitLevelLowerChoices(data.profit_level_lower_choices);
            setProfitLevelUpperChoices(data.profit_level_upper_choices);
            setNetAssetsLevelLowerChoices(data.net_assets_level_lower_choices);
            setNetAssetsLevelUpperChoices(data.net_assets_level_upper_choices);
            setProfitLevelLowerChoices(data.profit_level_lower_choices);
            setProfitLevelUpperChoices(data.profit_level_upper_choices);
            setHasAcquisitionExperienceChoices(data.has_acquisition_experience_choices);
        };
        fetchData();
    }, []);

    useEffect(() => {
        if (user?.buyer) {
            setValue('name', user.buyer.name || '');
            setValue('company_code', user.buyer.company_code || '');
            setValue('is_sole_proprietor', user.buyer.is_sole_proprietor || false);
            setValue('icon_image', user.buyer.icon_image || '');
            setValue('target_area', user.buyer.area || []);
            setValue('target_company_industries', user.buyer.target_company_industries || []);
            setValue('number_of_employees', user.buyer.number_of_employees || '');
            setValue('target_company_employee_size_lower', user.buyer.target_company_employee_size_lower || '');
            setValue('target_company_employee_size_upper', user.buyer.target_company_employee_size_upper || '');
            setValue('target_company_purchase_amount_lower', user.buyer.target_company_purchase_amount_lower || '');
            setValue('target_company_purchase_amount_upper', user.buyer.target_company_purchase_amount_upper || '');
            setValue('target_company_revenue_scale_lower', user.buyer.target_company_revenue_scale_lower || '');
            setValue('target_company_revenue_scale_upper', user.buyer.target_company_revenue_scale_upper || '');
            setValue('target_company_profit_level_lower', user.buyer.target_company_profit_level_lower || '');
            setValue('target_company_profit_level_upper', user.buyer.target_company_profit_level_upper || '');
            setValue('target_company_net_assets_level_lower', user.buyer.target_company_net_assets_level_lower || '');
            setValue('target_company_net_assets_level_upper', user.buyer.target_company_net_assets_level_upper || '');
            setValue('target_company_industry_details', user.buyer.target_company_industry_details || '');
            setValue('other_buying_points', user.buyer.other_buying_points || '');
            setValue('has_acquisition_experience', user.buyer.has_acquisition_experience || '');
            setValue('business_description', user.buyer.business_description || '');
            setValue('revenue_scale', user.buyer.revenue_scale || '');
            setValue('business_description', user.buyer.business_description || '');
            setValue('question', user.buyer.question || '');
            setValue('hp_url', user.buyer.hp_url || '');
            setValue('fee_structure', user.buyer.fee_structure || false);
            setValue('other_buyers', user.buyer.other_buyers || false);
            setValue('contract_status', user.buyer.contract_status || false);
            setValue('process_flow', user.buyer.process_flow || false);
            setValue('business_synergy', user.buyer.business_synergy || false);
            setValue('support_system', user.buyer.support_system || false);
            setValue('fee_structure_txt', user.buyer.fee_structure_txt || '');
            setValue('other_buyers_txt', user.buyer.other_buyers_txt || '');
            setValue('contract_status_txt', user.buyer.contract_status_txt || '');
            setValue('process_flow_txt', user.buyer.process_flow_txt || '');
            setValue('business_synergy_txt', user.buyer.business_synergy_txt || '');
            setValue('support_system_txt', user.buyer.support_system_txt || '');
        }
    }, [user]);

    const submit = handleSubmit(async (data) => {
        setLoading(true);
        try {
            await buyerEditRepository.editProfile({
                ...data,
            });
            await refetchUser();
            setShowSnackbar(true);
            setTimeout(() => window.location.replace('/buyer/registration/user_profile/'), 1500);
        } finally {
            setLoading(false);
            setShwAgreementDialog(false);
        }
    });

    return {
        user,
        control,
        errors,
        loading,
        showSnackbar,
        areaChoices,
        employeeChoices,
        revenueChoices,
        employeeLowerChoices,
        employeeUpperChoices,
        revenueScaleLowerChoices,
        revenueScaleUpperChoices,
        industryChoices,
        profitLevelLowerChoices,
        profitLevelUpperChoices,
        netAssetsLevelLowerChoices,
        netAssetsLevelUpperChoices,
        purchaseAmountLowerChoices,
        purchaseAmountUpperChoices,
        hasAcquisitionExperienceChoices,
        shwAgreementDialog,
        setShwAgreementDialog,
        setShowSnackbar,
        submit,
        getValues,
        setValue,
        watch,
        onCompanySelect,
    };
};
